<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();
const LABELS_REMOVED_ON_RESOLVE_BY_DEFAULT = new Set([
  'aguardando_atendimento',
  'aguardando_agendamento',
]);

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);
const labelCleanupDialogRef = ref(null);
const pendingResolveContext = ref(null);
const selectedLabelCleanupMode = ref('default');
const selectedLabelsToRemove = ref([]);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);
const currentLabels = computed(() =>
  Array.isArray(currentChat.value?.labels) ? currentChat.value.labels : []
);

const normalizeLabel = label =>
  String(label ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const defaultLabelsToRemove = computed(() =>
  currentLabels.value.filter(label => {
    const normalizedLabel = normalizeLabel(label);
    return (
      normalizedLabel.startsWith('aguardando_') ||
      LABELS_REMOVED_ON_RESOLVE_BY_DEFAULT.has(normalizedLabel)
    );
  })
);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const dropdownButtonColor = computed(() => {
  if (isOpen.value) return 'teal';
  if (isResolved.value) return 'blue';
  return 'slate';
});

const dropdownButtonClass = computed(() => {
  if (isOpen.value) {
    return '!bg-n-teal-10 !text-white hover:enabled:!bg-n-teal-11 ltr:border-l rtl:border-r border-white/15';
  }

  if (isResolved.value) {
    return '!bg-n-blue-10 !text-white hover:enabled:!bg-n-blue-11 ltr:border-l rtl:border-r border-white/15';
  }

  return '';
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = async (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  await store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const updateConversationLabels = labels => {
  store.dispatch('updateConversation', {
    ...currentChat.value,
    labels,
  });
};

const removeLabelsFromConversation = async labelsToRemove => {
  const labels = currentLabels.value;
  if (!labelsToRemove.length || !labels.length) return;

  const labelsToRemoveSet = new Set(labelsToRemove);
  const labelsToKeep = labels.filter(label => !labelsToRemoveSet.has(label));

  await store.dispatch('conversationLabels/update', {
    conversationId: currentChat.value.id,
    labels: labelsToKeep,
  });
  updateConversationLabels(labelsToKeep);
};

const resolveConversation = async ({
  snoozedUntil = null,
  customAttributes = null,
  labelsToRemove = [],
} = {}) => {
  try {
    isLoading.value = true;
    await removeLabelsFromConversation(labelsToRemove);
    await toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      snoozedUntil,
      customAttributes
    );
  } finally {
    isLoading.value = false;
  }
};

const resetLabelCleanupDialog = () => {
  pendingResolveContext.value = null;
  selectedLabelCleanupMode.value = 'default';
  selectedLabelsToRemove.value = [];
};

const askLabelCleanupBeforeResolve = (context = {}) => {
  if (!currentLabels.value.length) {
    resolveConversation(context);
    return;
  }

  pendingResolveContext.value = context;
  selectedLabelCleanupMode.value = defaultLabelsToRemove.value.length
    ? 'default'
    : 'none';
  selectedLabelsToRemove.value = [...defaultLabelsToRemove.value];
  labelCleanupDialogRef.value?.open();
};

const resolveLabelsForSelectedMode = () => {
  if (selectedLabelCleanupMode.value === 'all') return currentLabels.value;
  if (selectedLabelCleanupMode.value === 'select') {
    return selectedLabelsToRemove.value;
  }
  if (selectedLabelCleanupMode.value === 'default') {
    return defaultLabelsToRemove.value;
  }
  return [];
};

const confirmLabelCleanupAndResolve = async () => {
  const context = pendingResolveContext.value || {};
  const labelsToRemove = resolveLabelsForSelectedMode();
  labelCleanupDialogRef.value?.close();
  await resolveConversation({ ...context, labelsToRemove });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    askLabelCleanupBeforeResolve({
      snoozedUntil: context.snoozedUntil,
      customAttributes: mergedAttributes,
    });
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    askLabelCleanupBeforeResolve();
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        :aria-label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="teal"
        icon="i-lucide-check-circle-2"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 !bg-n-teal-9 !text-white hover:enabled:!bg-n-teal-10 !w-8 !px-0 md:!w-auto md:!px-3 [&_.button-label]:hidden md:[&_.button-label]:inline"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        :aria-label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="blue"
        icon="i-lucide-rotate-ccw"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 !bg-n-blue-9 !text-white hover:enabled:!bg-n-blue-10 !w-8 !px-0 md:!w-auto md:!px-3 [&_.button-label]:hidden md:[&_.button-label]:inline"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        :aria-label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="blue"
        icon="i-lucide-message-circle"
        no-animation
        class="!w-8 !px-0 md:!w-auto md:!px-3 [&_.button-label]:hidden md:[&_.button-label]:inline"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :class="dropdownButtonClass"
        :color="dropdownButtonColor"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <Dialog
      ref="labelCleanupDialogRef"
      width="md"
      :title="t('CONVERSATION.LABEL_CLEANUP.TITLE')"
      :description="t('CONVERSATION.LABEL_CLEANUP.DESCRIPTION')"
      :confirm-button-label="t('CONVERSATION.LABEL_CLEANUP.CONFIRM')"
      :cancel-button-label="t('CONVERSATION.LABEL_CLEANUP.CANCEL')"
      :is-loading="isLoading"
      @confirm="confirmLabelCleanupAndResolve"
      @close="resetLabelCleanupDialog"
    >
      <div class="flex flex-col gap-4">
        <div class="grid gap-2">
          <label
            class="flex gap-3 items-start p-3 rounded-lg border cursor-pointer border-n-weak hover:bg-n-alpha-2"
          >
            <input
              v-model="selectedLabelCleanupMode"
              type="radio"
              value="default"
              class="mt-0.5"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.LABEL_CLEANUP.DEFAULT.TITLE') }}
              </span>
              <span class="block text-sm text-n-slate-11">
                {{ t('CONVERSATION.LABEL_CLEANUP.DEFAULT.DESCRIPTION') }}
              </span>
            </span>
          </label>
          <label
            class="flex gap-3 items-start p-3 rounded-lg border cursor-pointer border-n-weak hover:bg-n-alpha-2"
          >
            <input
              v-model="selectedLabelCleanupMode"
              type="radio"
              value="all"
              class="mt-0.5"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.LABEL_CLEANUP.ALL.TITLE') }}
              </span>
              <span class="block text-sm text-n-slate-11">
                {{ t('CONVERSATION.LABEL_CLEANUP.ALL.DESCRIPTION') }}
              </span>
            </span>
          </label>
          <label
            class="flex gap-3 items-start p-3 rounded-lg border cursor-pointer border-n-weak hover:bg-n-alpha-2"
          >
            <input
              v-model="selectedLabelCleanupMode"
              type="radio"
              value="select"
              class="mt-0.5"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.LABEL_CLEANUP.SELECT.TITLE') }}
              </span>
              <span class="block text-sm text-n-slate-11">
                {{ t('CONVERSATION.LABEL_CLEANUP.SELECT.DESCRIPTION') }}
              </span>
            </span>
          </label>
          <label
            class="flex gap-3 items-start p-3 rounded-lg border cursor-pointer border-n-weak hover:bg-n-alpha-2"
          >
            <input
              v-model="selectedLabelCleanupMode"
              type="radio"
              value="none"
              class="mt-0.5"
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ t('CONVERSATION.LABEL_CLEANUP.NONE.TITLE') }}
              </span>
              <span class="block text-sm text-n-slate-11">
                {{ t('CONVERSATION.LABEL_CLEANUP.NONE.DESCRIPTION') }}
              </span>
            </span>
          </label>
        </div>

        <div
          v-if="selectedLabelCleanupMode === 'select'"
          class="flex flex-col gap-2 pt-2 border-t border-n-weak"
        >
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.LABEL_CLEANUP.CURRENT_LABELS') }}
          </p>
          <label
            v-for="label in currentLabels"
            :key="label"
            class="flex items-center gap-3 text-sm text-n-slate-12"
          >
            <Checkbox
              :model-value="selectedLabelsToRemove.includes(label)"
              @change="
                event => {
                  selectedLabelsToRemove = event.target.checked
                    ? [...selectedLabelsToRemove, label]
                    : selectedLabelsToRemove.filter(item => item !== label);
                }
              "
            />
            <span class="truncate">{{ label }}</span>
          </label>
        </div>
      </div>
    </Dialog>
  </div>
</template>
